import { GET_LOCATIONS_LIST, LIST_COUNTRIES_QUERY } from "queries/centralApi/centralApi";
import { CreatableAsyncPaginate } from "components/AsyncSelect/CreatableAsyncSelect";
import { Card, Col, Form, Input, Row } from "antd";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { AsyncSelect } from "@neonumy/ant-design";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import CardTitle from "components/CardTitle";

export default function HeadOfficeAddressForm(): React.ReactElement {
  const form = Form.useFormInstance();
  const { t } = useTranslation();

  const [countryCode, setCountryCode] = useState("FR");

  return (
    <Col xs={24} sm={12}>
      <Card
        title={<CardTitle icon={<FaMapMarkerAlt />} title={t("Head office information")} />}
        className="antd-newrai-card"
      >
        <Row gutter={[16, 16]}>
          <Form.Item name="addressId" hidden>
            <Input autoComplete="new-password" />
          </Form.Item>
          <Col xs={24} sm={4}>
            <Form.Item name="addressNr" label={t("Address Nr")}>
              <Input autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={3}>
            <Form.Item name="addressBtq" label={t("BTQ")}>
              <Input
                autoComplete="new-password"
                onChange={e => {
                  const value = e.target.value.toUpperCase();
                  if (value && !["BT", "Q", "B", "T"].includes(value)) {
                    form.setFieldsValue({ addressBtq: "" });
                  } else {
                    form.setFieldsValue({ addressBtq: value });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={17}>
            <Form.Item
              name="address1"
              label={t("Address")}
              rules={[{ required: true, message: t("This field is required") }]}
            >
              <Input maxLength={250} autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24}>
            <Form.Item name="address2" label={t("Additional address")}>
              <Input maxLength={250} autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label={t("Country")}
              name="country"
              initialValue={{ label: t("France"), value: "FR" }}
              rules={[{ required: true, message: t("This field is required") }]}
            >
              <AsyncSelect
                optionsFormat={item => ({
                  label: item.name,
                  value: item.code,
                  data: item,
                })}
                onChange={value => {
                  form.setFieldsValue({
                    city: undefined,
                    postalCode: undefined,
                  });
                  setCountryCode(value?.value);
                }}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listCountriesPaginated",
                  query: LIST_COUNTRIES_QUERY,
                  variables: {
                    search: "",
                    limit: 100,
                  },
                }}
                translations={{
                  placeholder: t(""),
                  loadingMessage: t("Loading"),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="postalCode"
              label={t("Postal code")}
              rules={[{ required: true, message: t("This field is required") }]}
            >
              <CreatableAsyncPaginate
                isDisabled={!countryCode}
                getOptionValue={(option: { id?: string; value?: string }) =>
                  option.id || option.value
                }
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listLocations",
                  query: GET_LOCATIONS_LIST,
                  variables: {
                    limit: 20,
                    orderBy: ["postal_code"],
                    countryCode: countryCode,
                  },
                }}
                optionsFormat={item => {
                  return {
                    label: `${item.postalCode} - ${item.city}`,
                    value: `${item.postalCode}`,
                    id: `${item.postalCode}-${item.city}`,
                    data: item,
                  };
                }}
                onCreateOption={value => {
                  form.setFieldsValue({
                    ...form.getFieldsValue(true),
                    postalCode: { label: value, value },
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="city"
              label={t("City")}
              rules={[{ required: true, message: t("This field is required") }]}
            >
              <CreatableAsyncPaginate
                isDisabled={!countryCode}
                getOptionValue={(option: { id?: string; value?: string }) =>
                  option.id || option.value
                }
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listLocations",
                  query: GET_LOCATIONS_LIST,
                  variables: {
                    limit: 20,
                    orderBy: ["city"],
                    countryCode: countryCode,
                  },
                }}
                optionsFormat={(item: { city?: string; postalCode?: string }) => ({
                  label: item.city as string,
                  value: item.city as string,
                  id: `${item.city}-${item.postalCode}`,
                  data: item,
                })}
                onCreateOption={value => {
                  form.setFieldsValue({
                    ...form.getFieldsValue(true),
                    city: { label: value, value },
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Col>
  );
}
